import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../i18n/LocaleProvider';

/* =============================================================================
   useField — the one validation engine every input in the library shares.
   -----------------------------------------------------------------------------
   The rule it exists to enforce (§10, "validation timing"): a pristine empty
   field never shows an error. The error is resolved from the *current* value,
   so once the field is revealed it re-resolves on every keystroke and clears
   the moment the value becomes valid — there is no second error state to keep
   in sync.

   Reveal is per-component and declared, which is what the spec asks for
   ("declare per component when the error appears"):

     - 'blur'   — text, email, url, textarea, search, number. The user sees the
                  error on the first blur (or on submit), then live.
     - 'change' — money and percentage. The spec requires the invalid state as
                  the value is typed, because the formatted string is what the
                  user is watching. Still never fires on an empty, untouched
                  field.
   ============================================================================= */

/** Returns an error message for an invalid value, or `null` when it is valid. */
export type FieldValidate = (value: string) => string | null;

export const REQUIRED_MESSAGE = 'This field is required';
export const DEFAULT_SUCCESS_DURATION = 2600;

export interface UseFieldOptions {
  /** The controlled value. Omit to let the hook own the state. */
  value?: string;
  /** Starting value when the hook owns the state. */
  defaultValue?: string;
  /** Called with every new value, controlled or not. */
  onChange?: (value: string) => void;
  /** Type validator. Never called for an empty value. */
  validate?: FieldValidate;
  /** Makes "empty" invalid, once revealed. */
  required?: boolean;
  requiredMessage?: string;
  validateOn?: 'blur' | 'change';
  /**
   * Bumped by the form on each submit attempt. Past the first bump every field
   * is revealed, which is how a submit reveals errors on fields the user never
   * blurred.
   */
  submitCount?: number;
  /** Inline-save latch: the consumer raises it after a successful save. */
  success?: boolean;
  successDuration?: number;
}

export interface UseFieldResult {
  value: string;
  setValue: (next: string) => void;
  /** True once the field has been blurred, marked, or a submit was attempted. */
  touched: boolean;
  /** Reveals the field without a blur — the submit path uses this. */
  markTouched: () => void;
  /** The blur handler to spread onto the control. */
  onBlur: () => void;
  /** The message to render right now, or `null`. */
  error: string | null;
  /** `error !== null`, for `aria-invalid` and the control's error styling. */
  invalid: boolean;
  /** True while the "Saved!" message should show. Auto-clears. */
  success: boolean;
  reset: () => void;
}

export function useField({
  value,
  defaultValue = '',
  onChange,
  validate,
  required = false,
  requiredMessage,
  validateOn = 'blur',
  submitCount = 0,
  success: successRequested = false,
  successDuration = DEFAULT_SUCCESS_DURATION,
}: UseFieldOptions = {}): UseFieldResult {
  const { t } = useTranslation();
  const [internal, setInternal] = useState(defaultValue);
  const [touchedState, setTouchedState] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;

  // Latest callback identity, so `setValue` stays stable and a caller may pass
  // an inline closure without re-creating it on every render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) setInternal(next);
      onChangeRef.current?.(next);
    },
    [isControlled],
  );

  const markTouched = useCallback(() => setTouchedState(true), []);

  const touched = touchedState || submitCount > 0;

  // Resolved, not stored: a validity that lives in state is a second source of
  // truth that drifts from the value.
  const revealed = validateOn === 'change' ? touched || current.trim() !== '' : touched;
  let error: string | null = null;
  if (revealed) {
    // The caller's own wording wins; the default is the design system's, and it
    // is resolved here rather than at the destructuring above so that it follows
    // the interface language instead of being frozen in the one it was written
    // in. `REQUIRED_MESSAGE` stays exported for a consumer that wants the English.
    if (current.trim() === '') error = required ? (requiredMessage ?? t('ds.form.required')) : null;
    else if (validate) error = validate(current);
  }

  useEffect(() => {
    if (!successRequested) {
      setSuccessVisible(false);
      return;
    }
    setSuccessVisible(true);
    const timer = window.setTimeout(() => setSuccessVisible(false), successDuration);
    return () => window.clearTimeout(timer);
  }, [successRequested, successDuration]);

  const reset = useCallback(() => {
    if (!isControlled) setInternal(defaultValue);
    setTouchedState(false);
    setSuccessVisible(false);
  }, [isControlled, defaultValue]);

  return {
    value: current,
    setValue,
    touched,
    markTouched,
    onBlur: markTouched,
    error,
    invalid: error !== null,
    // A field that is currently invalid never claims to have been saved.
    success: successVisible && error === null,
    reset,
  };
}
